/**
 * OPUS-913 — Edit profile modal (ported from the Claude Design handoff
 * profile.jsx). Editable name / username / email + preset/initials avatar
 * picker with colour swatches. Presentational: parent supplies `profile` and
 * handles `onSave(form)`.
 */
import React, { useState, useEffect } from "react";
import { Modal, Field, TextInput, UserAvatar, Button, Icon, Badge, PRESET_AVATARS, AVATAR_COLORS } from "../common/opus";

export default function ProfileModal({ open, onClose, profile, renderers = [], onSave }) {
  const [form, setForm] = useState(profile || { name: "", username: "", email: "", org: "", avatar: { type: "initials", color: "#FF455C" } });
  const [touched, setTouched] = useState(false);
  useEffect(() => { if (open) { setForm(profile || { name: "", username: "", email: "", org: "", avatar: { type: "initials", color: "#FF455C" } }); setTouched(false); } }, [open, profile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAvatar = (patch) => setForm((f) => ({ ...f, avatar: { ...(f.avatar || {}), ...patch } }));
  const nameErr = touched && !String(form.name || "").trim() ? "Name is required." : null;
  const emailValid = !form.email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email);
  const emailErr = touched && !emailValid ? "Enter a valid email address." : null;

  function submit() {
    setTouched(true);
    if (!String(form.name || "").trim() || !emailValid) return;
    onSave && onSave(form);
    onClose && onClose();
  }

  const initials = (String(form.name || "?").trim().split(/[\s_]+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()) || "?";
  const av = form.avatar || { type: "initials", color: "#FF455C" };

  return (
    <Modal open={open} onClose={onClose} title="Edit profile" subtitle="Update your details and choose an avatar." icon="users" width={560}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" icon="check" onClick={submit}>Save changes</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 16px", background: "var(--surface-0)", border: "1px solid var(--border)", borderRadius: "var(--r-md)" }}>
          <UserAvatar profile={form} size={56} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.name || "Your name"}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>@{form.username || "username"} · {form.org}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Name" required error={nameErr}>
            <TextInput value={form.name} onChange={(v) => set("name", v)} placeholder="Jane Doe" icon="users" invalid={!!nameErr} />
          </Field>
          <Field label="Username" required helper="Lowercase, no spaces.">
            <TextInput value={form.username} onChange={(v) => set("username", v.toLowerCase().replace(/\s+/g, "_"))} placeholder="jane_doe" />
          </Field>
        </div>
        <Field label="Email" error={emailErr} helper={!emailErr ? "Used for sign-in and notifications." : null}>
          <TextInput value={form.email} onChange={(v) => set("email", v)} placeholder="jane@ae.live" type="email" invalid={!!emailErr} />
        </Field>

        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)", marginBottom: 10 }}>Avatar</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <button type="button" onClick={() => setAvatar({ type: "initials" })} title="Use initials"
              style={{ width: 46, height: 46, borderRadius: "30%", cursor: "pointer", padding: 0,
                background: av.color || "#FF455C", color: "#fff", fontSize: 16, fontWeight: 700, fontFamily: "var(--font-sans)",
                border: av.type === "initials" ? "2px solid #fff" : "2px solid transparent", boxShadow: av.type === "initials" ? "0 0 0 2px var(--coral)" : "none" }}>
              {initials}
            </button>
            {PRESET_AVATARS.map((p) => {
              const active = av.type === "preset" && av.icon === p.icon && av.color === p.color;
              return (
                <button key={p.id} type="button" onClick={() => setAvatar({ type: "preset", icon: p.icon, color: p.color })} title="Preset avatar"
                  style={{ width: 46, height: 46, borderRadius: "30%", cursor: "pointer", padding: 0, background: p.color, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: active ? "2px solid #fff" : "2px solid transparent", boxShadow: active ? "0 0 0 2px var(--coral)" : "none" }}>
                  <Icon name={p.icon} size={22} />
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
            <span style={{ fontSize: 12, color: "var(--text-dim)", whiteSpace: "nowrap" }}>Initials colour</span>
            <div style={{ display: "flex", gap: 7 }}>
              {AVATAR_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setAvatar({ type: "initials", color: c })} title={c}
                  style={{ width: 22, height: 22, borderRadius: "50%", cursor: "pointer", background: c,
                    border: (av.type === "initials" && av.color === c) ? "2px solid #fff" : "2px solid transparent", boxShadow: (av.type === "initials" && av.color === c) ? "0 0 0 2px var(--border-strong)" : "none" }} />
              ))}
            </div>
          </div>
        </div>

        {/* OPUS-913: the user's own renderers (incl. their preview renderer) */}
        {renderers && renderers.length > 0 && (
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)", marginBottom: 10 }}>Your renderers</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {renderers.map((r) => {
                const online = String(r.connectivity_status || "").toLowerCase() === "online";
                const isPreview = (r.allocation_scope || "") === "preview";
                return (
                  <div key={r.renderer_id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", background: "var(--surface-0)", border: "1px solid var(--border)", borderRadius: "var(--r-md)" }}>
                    <span style={{ width: 30, height: 30, borderRadius: "var(--r-sm)", background: "rgba(255,69,92,0.12)", color: "var(--coral)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="rendererList" size={16} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 650, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.renderer_name || r.renderer_id}</span>
                        {isPreview && <Badge tone="violet">Preview</Badge>}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--text-dim)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.renderer_id}</div>
                    </div>
                    <Badge tone={online ? "green" : "neutral"} dot>{online ? "Online" : "Offline"}</Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
